import { useMemo } from 'react';
import { RankedBars } from '../../charts/RankedBars';
import { Card, ChartCard, DataTable, Stat, StatusChip, type Column } from '../../components/data';
import { ErrorBanner, Loading } from '../../components/ui';
import { num, pct, relativeDays, snakeLabel, urgency } from '../../lib/format';
import { useResource } from '../../lib/hooks';
import type { Employee } from '../../lib/types';
import { criticalityBand, sopBand, type KpiOption, type SopRow, type SopsData } from './adminTypes';
import { RegisterPanel, formatIsoShort, type RegisterSpec } from './adminUi';

const SOP_CATEGORY = [
  'service_desk', 'procurement', 'depot', 'finance', 'hr', 'security', 'onboarding', 'other',
] as const;
const CRITICALITY = ['critical', 'high', 'medium', 'low'] as const;
const SOP_STATUS = ['active', 'draft', 'under_review', 'retired'] as const;

/**
 * The SOP register.
 *
 * A REGISTER, not a document library, and the distinction is the whole design.
 * This answers "which procedures are out of date and who do I chase"; the
 * documents themselves live wherever they live — in the operations manual, in the
 * AM module's uploaded SOPs, in SharePoint — and `DocumentLink` points at them. A
 * register that required the document to be uploaded here first would record
 * fewer procedures than exist, which is the opposite of what a register is for.
 * See the header of migration 048.
 *
 * Currency is measured over the live SOPs that HAVE a review date. One with no
 * date set is neither current nor overdue — it is unmanaged, which is its own
 * count, because rolling it into either would hide it.
 */
export function AdminSops({ canWrite, onChanged }: { canWrite: boolean; onChanged: () => void }) {
  const analytics = useResource<SopsData>('/admin/analytics/sops');
  const employees = useResource<Employee[]>('/employees?status=active');
  const kpis = useResource<KpiOption[]>('/admin/pickers/kpis');

  const spec = useMemo<RegisterSpec<SopRow>>(
    () => ({
      resource: 'sops',
      title: 'The SOP register',
      singular: 'SOP',
      emptyTitle: 'No procedures registered yet',
      emptyBody:
        'List every procedure the company owns, who owns it, how critical it is and when it is next due for review. The document can live anywhere — link to it. What this register is for is knowing what has gone stale and who to ask about it.',
      searchHint: 'Code, title or owner',
      idOf: (row) => row.SopRegisterId,
      labelOf: (row) => `${row.SopCode} — ${row.Title}`,
      filters: [
        { param: 'status', label: 'Status', options: SOP_STATUS },
        { param: 'category', label: 'Category', options: SOP_CATEGORY },
        { param: 'criticality', label: 'Criticality', options: CRITICALITY },
      ],
      columns: [
        {
          key: 'SopCode',
          label: 'SOP',
          render: (row) => (
            <div>
              <div className="admin-cell-title">
                {row.DocumentLink ? (
                  <a href={row.DocumentLink} target="_blank" rel="noreferrer noopener">
                    {row.SopCode} — {row.Title}
                  </a>
                ) : (
                  `${row.SopCode} — ${row.Title}`
                )}
              </div>
              <div className="xs muted">
                {snakeLabel(row.Category)} · v{row.Version}
                {row.LinkedKpiName ? ` · drives ${row.LinkedKpiName}` : ''}
              </div>
            </div>
          ),
        },
        {
          key: 'Criticality',
          label: 'Criticality',
          render: (row) => (
            <StatusChip band={criticalityBand(row.Criticality)}>{snakeLabel(row.Criticality)}</StatusChip>
          ),
        },
        {
          key: 'Status',
          label: 'Status',
          render: (row) => <StatusChip band={sopBand(row.Status)}>{snakeLabel(row.Status)}</StatusChip>,
        },
        {
          key: 'OwnerName',
          label: 'Owner',
          render: (row) => row.OwnerName ?? <span className="admin-warn-text">unassigned</span>,
        },
        {
          key: 'LastReviewed',
          label: 'Last reviewed',
          render: (row) =>
            row.LastReviewed ? (
              formatIsoShort(row.LastReviewed)
            ) : (
              <span className="admin-warn-text">never</span>
            ),
        },
        {
          key: 'NextReview',
          label: 'Next review',
          render: (row) => {
            /* Retired procedures are not overdue for anything — showing a red
               chip against one would be chasing work nobody should do. */
            if (row.Status === 'retired') return <span className="sub">retired</span>;
            if (!row.NextReview) return <span className="admin-warn-text">not scheduled</span>;
            return (
              <div className="admin-due">
                <span>{formatIsoShort(row.NextReview)}</span>
                <StatusChip band={urgency(row.DaysToReview)}>{relativeDays(row.DaysToReview)}</StatusChip>
              </div>
            );
          },
        },
        {
          key: 'ReviewFrequencyMonths',
          label: 'Cycle',
          align: 'right',
          render: (row) => `${row.ReviewFrequencyMonths} mo`,
        },
      ],
      fields: [
        { name: 'sopCode', label: 'SOP code', kind: 'text' },
        { name: 'version', label: 'Version', kind: 'text' },
        { name: 'title', label: 'Title', kind: 'text', wide: true },
        { name: 'category', label: 'Category', kind: 'select', options: SOP_CATEGORY },
        { name: 'criticality', label: 'Criticality', kind: 'select', options: CRITICALITY },
        {
          name: 'ownerEmployeeId',
          label: 'Owner',
          kind: 'select',
          optional: true,
          placeholder: 'Unassigned',
          hint: 'From the shared directory, so a procedure is noticed when its owner leaves.',
          choices: (employees.data ?? []).map((e) => ({
            value: String(e.EmployeeId),
            label: e.FullName,
          })),
        },
        { name: 'status', label: 'Status', kind: 'select', options: SOP_STATUS },
        { name: 'reviewFrequencyMonths', label: 'Review cycle (months)', kind: 'int' },
        { name: 'lastReviewed', label: 'Last reviewed', kind: 'date' },
        {
          name: 'nextReview',
          label: 'Next review',
          kind: 'date',
          hint: 'Set independently of the cycle, so a procedure can be pulled forward after an incident.',
        },
        {
          name: 'linkedKpiId',
          label: 'Measure it drives',
          kind: 'select',
          optional: true,
          placeholder: 'None',
          choices: (kpis.data ?? []).map((k) => ({
            value: String(k.AdminKpiId),
            label: `${k.KpiCode} — ${k.Name}`,
          })),
        },
        { name: 'documentLink', label: 'Document link', kind: 'text', wide: true },
        { name: 'notes', label: 'Notes', kind: 'textarea', wide: true },
      ],
      blank: {
        sopCode: '',
        version: '1.0',
        title: '',
        category: 'service_desk',
        criticality: 'medium',
        ownerEmployeeId: '',
        status: 'active',
        reviewFrequencyMonths: 12,
        lastReviewed: '',
        nextReview: '',
        linkedKpiId: '',
        documentLink: '',
        notes: '',
      },
      toForm: (row) => ({
        sopCode: row.SopCode,
        version: row.Version,
        title: row.Title,
        category: row.Category,
        criticality: row.Criticality,
        ownerEmployeeId: row.OwnerEmployeeId ?? '',
        status: row.Status,
        reviewFrequencyMonths: row.ReviewFrequencyMonths,
        lastReviewed: row.LastReviewed?.slice(0, 10) ?? '',
        nextReview: row.NextReview?.slice(0, 10) ?? '',
        linkedKpiId: row.LinkedKpiId ?? '',
        documentLink: row.DocumentLink ?? '',
        notes: row.Notes,
      }),
    }),
    [employees.data, kpis.data],
  );

  const reload = () => {
    analytics.reload();
    onChanged();
  };

  const d = analytics.data;
  const s = d?.summary;

  return (
    <div className="admin-stack">
      {analytics.error && <ErrorBanner message={analytics.error} />}
      {!d && analytics.loading && <Loading label="Loading the SOP register" />}

      {d && s && s.total > 0 && (
        <>
          <div className="grid grid-4">
            <Stat
              label="Currency"
              value={s.currencyPercent === null ? '—' : pct(s.currencyPercent, 0)}
              foot={
                s.currencyPercent === null
                  ? 'no SOP has a review date set'
                  : 'live SOPs inside their review date'
              }
            />
            <Stat
              label="Overdue for review"
              value={s.overdueReview}
              flagged={s.overdueReview > 0}
              foot={`${s.dueIn30} more due within 30 days`}
            />
            <Stat
              label="Never reviewed"
              value={s.neverReviewed}
              flagged={s.neverReviewed > 0}
              foot="written and not revisited"
            />
            <Stat
              label="Unmanaged"
              value={s.noReviewDate}
              flagged={s.noReviewDate > 0}
              /* Neither current nor overdue — the state that hides. */
              foot="live, with no review date at all"
            />
          </div>

          <div className="grid grid-4">
            <Stat label="Active" value={s.active} foot={`of ${num(s.total)} on the register`} />
            <Stat label="Draft" value={s.draft} foot="not yet in force" />
            <Stat label="Under review" value={s.underReview} foot="being rewritten" />
            <Stat
              label="Linked to a measure"
              value={s.linkedToKpi}
              foot={`of ${num(s.live)} live procedures`}
            />
          </div>

          <div className="admin-split">
            <ChartCard
              title="By category"
              subtitle="How many procedures each function owns, and how many have gone stale."
              table={{
                columns: [
                  {
                    key: 'category',
                    label: 'Category',
                    render: (row: SopsData['byCategory'][number]) => snakeLabel(row.category),
                  },
                  { key: 'count', label: 'SOPs', align: 'right' },
                  { key: 'overdue', label: 'Overdue', align: 'right' },
                ],
                rows: d.byCategory,
                rowKey: (row) => row.category,
              }}
            >
              <RankedBars
                rows={d.byCategory.map((c) => ({
                  label: snakeLabel(c.category),
                  value: c.count,
                  meta: c.overdue > 0 ? `${c.overdue} overdue` : 'all current',
                  /* Coloured by whether anything in the category is overdue —
                     the count alone does not say which categories need work. */
                  color: c.overdue > 0 ? 'var(--status-warning)' : undefined,
                }))}
                format={(v) => num(v)}
                labelWidth={140}
              />
            </ChartCard>

            <ChartCard
              title="By owner"
              subtitle="Who carries the review load. Unassigned procedures are their own row."
              table={{
                columns: [
                  { key: 'ownerName', label: 'Owner' },
                  { key: 'count', label: 'SOPs', align: 'right' },
                  { key: 'overdue', label: 'Overdue', align: 'right' },
                ],
                rows: d.byOwner,
                rowKey: (row) => row.ownerName,
              }}
            >
              <RankedBars
                rows={d.byOwner.map((o) => ({
                  label: o.ownerName,
                  value: o.count,
                  meta: o.overdue > 0 ? `${o.overdue} overdue` : 'all current',
                  color:
                    o.ownerName === 'Unassigned'
                      ? 'var(--status-serious)'
                      : o.overdue > 0
                        ? 'var(--status-warning)'
                        : undefined,
                }))}
                format={(v) => num(v)}
                labelWidth={148}
              />
            </ChartCard>
          </div>

          <ChartCard
            title="By criticality"
            subtitle="How much of the register is load-bearing."
            footer={
              s.critical > 0
                ? `${num(s.critical)} ${s.critical === 1 ? 'procedure is' : 'procedures are'} rated critical.`
                : undefined
            }
            table={{
              columns: [
                {
                  key: 'criticality',
                  label: 'Criticality',
                  render: (row: SopsData['byCriticality'][number]) => snakeLabel(row.criticality),
                },
                { key: 'count', label: 'SOPs', align: 'right' },
              ],
              rows: d.byCriticality,
              rowKey: (row) => row.criticality,
            }}
          >
            <RankedBars
              rows={d.byCriticality.map((c) => ({
                label: snakeLabel(c.criticality),
                value: c.count,
                /* Criticality IS a severity, so it takes the status ladder
                   rather than a categorical slot. */
                color: `var(--status-${criticalityBand(c.criticality)})`,
              }))}
              format={(v) => num(v)}
              labelWidth={132}
            />
          </ChartCard>

          <Card
            title="Reviews due"
            subtitle="Live procedures overdue or due within 60 days, soonest first."
          >
            <DataTable
              columns={DUE_COLUMNS}
              rows={d.dueSoon}
              rowKey={(row) => String(row.sopRegisterId)}
              empty={
                s.noReviewDate === s.live
                  ? 'No live procedure has a review date set, so nothing can be due.'
                  : 'No reviews are due in the next 60 days.'
              }
            />
          </Card>
        </>
      )}

      <RegisterPanel spec={spec} canWrite={canWrite} onChanged={reload} />
    </div>
  );
}

const DUE_COLUMNS: Column<SopsData['dueSoon'][number]>[] = [
  {
    key: 'sopCode',
    label: 'SOP',
    render: (row) => (
      <div>
        <div className="admin-cell-title">
          {row.sopCode} — {row.title}
        </div>
        <div className="xs muted">
          {snakeLabel(row.category)} · v{row.version}
          {row.linkedKpiName ? ` · drives ${row.linkedKpiName}` : ''}
        </div>
      </div>
    ),
  },
  {
    key: 'criticality',
    label: 'Criticality',
    render: (row) => (
      <StatusChip band={criticalityBand(row.criticality)}>{snakeLabel(row.criticality)}</StatusChip>
    ),
  },
  {
    key: 'status',
    label: 'Status',
    render: (row) => <StatusChip band={sopBand(row.status)}>{snakeLabel(row.status)}</StatusChip>,
  },
  {
    key: 'ownerName',
    label: 'Owner',
    render: (row) => row.ownerName ?? <span className="admin-warn-text">unassigned</span>,
  },
  {
    key: 'lastReviewed',
    label: 'Last reviewed',
    render: (row) =>
      row.lastReviewed ? formatIsoShort(row.lastReviewed) : <span className="admin-warn-text">never</span>,
  },
  {
    key: 'nextReview',
    label: 'Due',
    render: (row) => (
      <div className="admin-due">
        <span>{formatIsoShort(row.nextReview)}</span>
        <StatusChip band={urgency(row.daysToReview)}>{relativeDays(row.daysToReview)}</StatusChip>
      </div>
    ),
  },
];
